<script setup lang="ts">
import { ref, computed } from 'vue';
import type { TranslateResult } from 'vue-i18n';
import { useFieldBorder } from '@/common/customisation/composables/useFieldBorder';
import PlsText from './PlsText.vue';

export interface ValidationInputCustomisation {
  backgroundColor?: string;
  textColor?: string;
  placeholderColor?: string;
  borderColor?: string;
  errorColor?: string;
}

export interface ValidationInputProps {
  tag?: string;
  value?: string;
  type?: string;
  placeholder?: string | TranslateResult;
  disabled?: boolean;
  maxLength?: number;
  showError?: boolean;
  showErrorTextBlock?: boolean;
  errorMessage?: string | TranslateResult;
  customisation?: ValidationInputCustomisation;
}

const props = withDefaults(defineProps<ValidationInputProps>(), {
  tag: 'input',
  value: '',
  type: 'text',
  placeholder: '',
  maxLength: 524288, // default max length of input
  errorMessage: 'Please provide a valid input',
  showErrorTextBlock: true,
});

const emit = defineEmits<{
  (e: 'input', evt: string): void;
}>();

const focused = ref(false);
const { borderColor } = useFieldBorder();

const showError = computed(() => {
  return props.showError && !focused.value;
});

const inputStyle = computed(() => ({
  backgroundColor: props.customisation?.backgroundColor,
  color: props.customisation?.textColor,
  border: props.customisation?.borderColor
    ? `1px solid ${props.customisation?.borderColor}`
    : borderColor.value
      ? `1px solid ${borderColor.value}`
      : undefined,
  '--placeholder-color': props.customisation?.placeholderColor,
}));

const errorTextStyle = computed(() => ({
  color: props.customisation?.errorColor,
}));

function handleInputEvent(event: Event) {
  const input = event.target as HTMLInputElement;
  emit('input', input.value);
}
</script>

<template>
  <div class="validation-input-container">
    <!-- WARNING :is not working here, default value passed ignores due to dynamic behaviour of it -->
    <input
      v-if="tag === 'input'"
      :value="value"
      :type="type"
      :placeholder="`${placeholder}`"
      :maxlength="maxLength"
      :disabled="disabled"
      :style="inputStyle"
      :class="{ 'validation-input--error': showError }"
      class="input-radius theme-font-main validation-input"
      @input="handleInputEvent"
      @focus="focused = true"
      @blur="focused = false"
    />
    <textarea
      v-else-if="tag === 'textarea'"
      :value="value"
      :rows="4"
      :placeholder="`${placeholder}`"
      :maxlength="maxLength"
      :disabled="disabled"
      :style="inputStyle"
      :class="{ 'validation-input--error': showError }"
      class="input-radius theme-font-main validation-input validation-input--textarea"
      @input="handleInputEvent"
      @focus="focused = true"
      @blur="focused = false"
    ></textarea>

    <PlsText
      v-if="showErrorTextBlock"
      size-variant="caption"
      :class="{ 'validation-input__error-text--visible': showError }"
      class="validation-input__error-text"
      :style="errorTextStyle"
    >
      <span>*</span>
      {{ errorMessage }}
    </PlsText>
  </div>
</template>

<style scoped lang="scss">
.validation-input-container {
  position: relative;
}

.validation-input {
  width: 100%;
  padding: 12px;
  outline: none;

  &::placeholder {
    color: var(--placeholder-color, #9ca0ab);
  }

  // TODO: Add focus state styling (use customisation prop or theme)
  // &:focus {
  //   border-color: ...;
  // }

  // TODO: Add disabled state styling (use customisation prop or theme)
  // &:disabled {
  //   background-color: ...;
  //   cursor: not-allowed;
  // }

  &--textarea {
    resize: vertical;
  }

  // TODO: Add error state styling (use customisation prop or theme)
  // &--error {
  //   border-color: ...;
  // }
}

.validation-input__error-text {
  margin-top: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  &--visible {
    opacity: 1;
  }
}
</style>
